import Guid from "_shared/guids/guid";
import { chain } from "lodash";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

/**
 * One place "Prev / Next" can stop: a field still waiting on the reader, or one of the proposal's two
 * conversations while it holds unread comments.
 *
 * A conversation is one stop, not one per comment. A comment is not about any field, so there is no
 * field to stop it at; the stop is the Comments pane on that conversation's tab, scrolled to its oldest
 * unread comment. Each conversation is a stop of its own (#5824): the pane shows one tab at a time, so
 * one stop for both took the reader to one tab and gave the other tab's unread comments no stop.
 *
 * `isExternal` names the conversation as `CommentThread.isExternal` does: true is "All Stakeholders",
 * false is the reader's own party's "Reviewers".
 */
export type ProposalTraversalStop =
  | { kind: "field"; field: ProposalField }
  | { kind: "unreadComments"; isExternal: boolean };

/** How many comments the reader has not read in each of the proposal's two conversations. */
export type ConversationUnreadCounts = {
  /** The conversation both parties read: the pane's left tab. */
  allStakeholders: number;
  /** The conversation only the reader's own party reads: the pane's right tab. */
  ownTeam: number;
};

export const noUnreadComments: ConversationUnreadCounts = { allStakeholders: 0, ownTeam: 0 };

/** The stop on one conversation's unread comments. */
export function unreadCommentsTraversalStop(isExternal: boolean): ProposalTraversalStop {
  return { kind: "unreadComments", isExternal };
}

export function fieldTraversalStop(field: ProposalField): ProposalTraversalStop {
  return { kind: "field", field };
}

export function traversalStopsAreEqual(
  left: ProposalTraversalStop | undefined,
  right: ProposalTraversalStop | undefined,
): boolean {
  if (!left || !right) return false;
  if (left.kind === "unreadComments") return right.kind === "unreadComments" && left.isExternal === right.isExternal;
  if (right.kind === "unreadComments") return false;
  return left.field.isEqualTo(right.field);
}

/** Where the traverser stands, and the stops it walks from there. */
export type ProposalTraversal = {
  stops: ProposalTraversalStop[];
  /** Index into `stops`, or -1 when the reader is on no stop. */
  currentIndex: number;
};

/**
 * The stops "Prev / Next" walks, in order: every pending field in page order (tab, then field), then one
 * stop for each conversation with unread comments, in the order the pane's tabs show them — All
 * Stakeholders, then Reviewers — then back to the first field.
 *
 * Fields that share a tab and a name are rows of one table — a fee schedule's categories — and `rowOrder`
 * puts them in the order that table draws them, whether a row is pending for an issue, an unread change
 * or both (#6617). Without it they kept the order they were handed in, every issue before every unread
 * change, so Next jumped about the table. The field without an id, the table as a whole, comes before its
 * rows; a row the order does not name comes after the rows it does.
 *
 * A field pending for two reasons is one stop. Ids are compared as `ProposalField.isEqualTo` compares
 * them, case-insensitively: an unread change names its category by the stream's item id, and the stream
 * and the projection have not always agreed on casing.
 *
 * `lastStop` is where the traverser itself last landed. It stays in the order while the reader is still
 * on it, even once it has stopped being pending: arriving is what acknowledges an unread change, and
 * reading a conversation clears its count, so without this the stop the reader is looking at would drop
 * out from under them, the counter would lose their place, and Next would restart from the top instead
 * of moving on. A field stop counts as "still on it" only while it is the page's current field — a reader
 * who has since focused another field has left it. A conversation stop has no field, so it holds until
 * the next traversal replaces it.
 */
export function proposalTraversal(args: {
  pendingFields: ProposalField[];
  unreadCommentCounts?: ConversationUnreadCounts;
  currentField: ProposalField | undefined;
  lastStop?: ProposalTraversalStop;
  /** The rows of the page's tables, each in the order its table draws them. */
  rowOrder?: readonly ProposalField[];
}): ProposalTraversal {
  const { pendingFields, currentField, lastStop } = args;
  const rowOrder = args.rowOrder ?? [];
  const unreadCommentCounts = args.unreadCommentCounts ?? noUnreadComments;
  const heldStop =
    lastStop?.kind === "field" && !(currentField && lastStop.field.isEqualTo(currentField)) ? undefined : lastStop;

  const fields = [...pendingFields, ...(heldStop?.kind === "field" ? [heldStop.field] : [])];
  const categories = Object.values(ProposalFieldCategory);
  const names = Object.values(ProposalFieldName);
  const fieldStops = chain(fields)
    .uniqWith((left, right) => left.isEqualTo(right))
    .sortBy([
      (field) => categories.indexOf(field.category),
      (field) => names.indexOf(field.name),
      (field) => rowPosition(field, rowOrder),
    ])
    .map(fieldTraversalStop)
    .value();
  const conversationStops = [
    { stop: unreadCommentsTraversalStop(true), unread: unreadCommentCounts.allStakeholders },
    { stop: unreadCommentsTraversalStop(false), unread: unreadCommentCounts.ownTeam },
  ]
    .filter(({ stop, unread }) => unread > 0 || traversalStopsAreEqual(stop, heldStop))
    .map(({ stop }) => stop);
  const stops = [...fieldStops, ...conversationStops];

  const currentStop = heldStop ?? (currentField ? fieldTraversalStop(currentField) : undefined);
  return { stops, currentIndex: stops.findIndex((stop) => traversalStopsAreEqual(stop, currentStop)) };
}

/** A fee schedule's rows as traversal fields, given its category ids in the order its table draws them. */
export function feeScheduleRowOrder(categoryIds: readonly Guid[]): ProposalField[] {
  return categoryIds.map((categoryId) => ProposalField.FeeScheduleCategory(categoryId));
}

/** Where a field sorts among its table's rows: the table itself first, a row it does not draw last. */
function rowPosition(field: ProposalField, rowOrder: readonly ProposalField[]): number {
  if (!field.id) return -1;
  const position = rowOrder.findIndex((row) => row.isEqualTo(field));
  return position < 0 ? rowOrder.length : position;
}

/** The stop after the current one, wrapping to the first; the first stop when the reader is on none. */
export function nextTraversalStop({ stops, currentIndex }: ProposalTraversal): ProposalTraversalStop | undefined {
  if (stops.length === 0) return undefined;
  return stops[currentIndex < 0 ? 0 : (currentIndex + 1) % stops.length];
}

/** The stop before the current one, wrapping to the last; the last stop when the reader is on none. */
export function previousTraversalStop({ stops, currentIndex }: ProposalTraversal): ProposalTraversalStop | undefined {
  if (stops.length === 0) return undefined;
  return stops[currentIndex < 0 ? stops.length - 1 : (currentIndex - 1 + stops.length) % stops.length];
}

/**
 * "3 of 7": where the reader is in the walk, so a wrap back to the start is visible rather than
 * disorienting. A dash stands in for the position when the reader is on no stop.
 */
export function traversalPositionLabel({ stops, currentIndex }: ProposalTraversal): string {
  return `${currentIndex < 0 ? "–" : currentIndex + 1} of ${stops.length}`;
}
